<script>
import { ElNotification } from "element-plus";

export default {
  name: "QuarterReport",
  data() {
    return {
      loading: false,
      loadingBalance: false,
      uploading: false,
      uploadedFileName: "",
      fin_year: new Date().getFullYear(),
      uploadAccept:
        ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel",
    };
  },
  computed: {
    years() {
      const currentYear = new Date().getFullYear();
      const years = [];

      for (let i = 2024; i <= currentYear; i += 1) {
        years.push(i);
      }

      return years;
    },
  },
  methods: {
    isExcelFile(file) {
      if (!file) {
        return false;
      }

      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const fileName = String(file.name || "").toLowerCase();

      return (
        allowedTypes.includes(file.type) ||
        fileName.endsWith(".xlsx") ||
        fileName.endsWith(".xls")
      );
    },
    resetFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    triggerFileUpload() {
      if (this.loading || this.uploading) {
        return;
      }

      this.$refs.fileInput?.click();
    },
    async handleFileChange(event) {
      const file = event?.target?.files?.[0];

      if (!file) {
        return;
      }

      if (!this.isExcelFile(file)) {
        ElNotification({
          title: "Xatolik",
          message: "Faqat Excel fayllar (.xlsx, .xls) yuklash mumkin",
          type: "error",
        });
        this.resetFileInput();
        return;
      }

      this.uploading = true;

      try {
        const formData = new FormData();
        formData.append("file", file);

        await this.$http.post("api/v1/quarters/statistics-excel", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.uploadedFileName = file.name;

        ElNotification({
          title: "Muvaffaqiyatli",
          message: "Mahalla budjetining yillik prognozi muvaffaqiyatli yuklandi",
          type: "success",
        });
      } catch ({ response }) {
        ElNotification({
          title: "Xatolik",
          message:
            response?.data?.message || "Fayl yuklashda xatolik sodir bo'ldi!",
          type: "error",
        });
      } finally {
        this.uploading = false;
        this.resetFileInput();
      }
    },
    downloadBalanceExcel() {
      this.loadingBalance = true;
      this.$http
        .get("api/v1/quarter-payments/balances-excel", {
          responseType: "blob",
        })
        .then((res) => {
          const url = URL.createObjectURL(
            new Blob([res.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
            })
          );
          const header = res.headers.get("Content-Disposition");
          const parts = header.split(";");
          const fileName = parts[1].split("=")[1];
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(({ response }) => {
          ElNotification({
            title: "Ogohlantirish",
            message:
              response?.data?.message ||
              "Ma'lumot yuklashda xatolik sodir bo'ldi!",
            type: "warning",
          });
        })
        .finally(() => {
          this.loadingBalance = false;
        });
    },
    downloadExcel() {
      this.loading = true;
      this.$http
        .get(`api/v1/quarter-payments/excel?fin_year=${this.fin_year}`, {
          responseType: "blob",
        })
        .then((res) => {
          const url = URL.createObjectURL(
            new Blob([res.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
            })
          );
          const header = res.headers.get("Content-Disposition");
          const parts = header.split(";");
          const fileName = parts[1].split("=")[1];
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(({ response }) => {
          ElNotification({
            title: "Ogohlantirish",
            message:
              response?.data?.message ||
              "Ma'lumot yuklashda xatolik sodir bo'ldi!",
            type: "warning",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<template>
  <div>
    <div class="actions-panel">
      <input
        ref="fileInput"
        class="hidden-file-input"
        type="file"
        :accept="uploadAccept"
        @change="handleFileChange"
      />

      <div class="action-cards">
        <div class="action-card download-card">
          <div class="action-card__body">
            <h3 class="action-card__title">Excel yuklab olish</h3>
            <p class="action-card__description">
              Kerakli yilni tanlang va hisobotni Excel formatida yuklab oling.
            </p>
          </div>

          <div class="action-card__controls download-controls">
            <el-select
              id="year"
              v-model="fin_year"
              size="large"
              placeholder="Yil"
              class="year-select"
            >
              <el-option
                v-for="y in years"
                :key="y"
                :value="y"
                :label="y"
              />
            </el-select>

            <el-button
              size="large"
              :loading="loading"
              :disabled="loading || uploading"
              class="action-button"
              type="success"
              @click="downloadExcel"
            >
              <img
                class="action-button__icon"
                alt=""
                src="@/assets/images/icons/download.svg"
              />
              Excel yuklab olish
            </el-button>
          </div>
        </div>

        <div class="action-card upload-card">
          <div class="action-card__body">
            <h3 class="action-card__title">
              Mahalla budjetining yillik prognozini yuklash
            </h3>
            <p class="action-card__description">
              Tayyor Excel faylni shu alohida bo'lim orqali yuklang.
            </p>
          </div>

          <div class="action-card__controls">
            <el-button
              size="large"
              :loading="uploading"
              :disabled="loading || uploading"
              class="action-button upload-button"
              @click="triggerFileUpload"
            >
              <img
                class="action-button__icon"
                alt=""
                src="@/assets/images/icons/file-attach-white.svg"
              />
              Prognoz faylini yuklash
            </el-button>

            <span
              v-if="uploadedFileName"
              class="upload-success-icon"
              aria-label="Fayl yuklandi"
              title="Fayl yuklandi"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden="true"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                />
              </svg>
            </span>
          </div>
        </div>

        <div class="action-card balance-card">
          <div class="action-card__body">
            <h3 class="action-card__title">Mahalla balansini yuklab olish</h3>
            <p class="action-card__description">
              Mahalla balansi hisobotini Excel formatida yuklab oling.
            </p>
          </div>

          <div class="action-card__controls">
            <el-button
              size="large"
              :loading="loadingBalance"
              :disabled="loadingBalance || uploading"
              class="action-button balance-button"
              @click="downloadBalanceExcel"
            >
              <img
                class="action-button__icon"
                alt=""
                src="@/assets/images/icons/download.svg"
              />
              Balansni yuklab olish
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.actions-panel {
  display: flex;
  gap: 16px;
}

.action-cards {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.action-card {
  max-width: 360px;
  min-width: 300px;
  min-height: 168px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid #dbe3ef;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
}

.download-card {
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.06);
}

.upload-card {
  border: 1px dashed #f59e0b;
  background: linear-gradient(180deg, #fffaf0 0%, #fff4db 100%);
}

.action-card__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-card__eyebrow {
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.action-card__title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: #111827;
}

.action-card__description {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #4b5563;
}

.action-card__controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.download-controls {
  justify-content: space-between;
}

.year-select {
  width: 120px;
}

.action-button {
  min-height: 44px;
}

.action-button__icon {
  margin-right: 10px;
}

.upload-button {
  background: #d97706;
  border-color: #d97706;
  color: #ffffff;
}

.upload-button:hover,
.upload-button:focus {
  background: #b45309;
  border-color: #b45309;
  color: #ffffff;
}

.balance-card {
  border: 1px solid #bfdbfe;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
}

.balance-button {
  background: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
}

.balance-button:hover,
.balance-button:focus {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #ffffff;
}

.upload-success-icon {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #86efac;
  flex-shrink: 0;
}

.hidden-file-input {
  display: none;
}

@media (max-width: 768px) {
  .action-card {
    min-height: auto;
  }

  .action-card__controls,
  .download-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .year-select {
    width: 100%;
  }

  .action-button {
    width: 100%;
  }
}
</style>
